import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Building2, ShieldCheck, KeyRound } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Tenant + branding state
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const branding = selectedTenant?.branding || {};
  const samlEnabled = !!selectedTenant?.saml_enabled;
  const oidcEnabled = !!selectedTenant?.oidc_enabled;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/tenants`);
        setTenants(data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  // When user types email, attempt domain → tenant resolution
  useEffect(() => {
    const at = email.indexOf('@');
    if (at < 0 || selectedTenant) return;
    const domain = email.slice(at + 1).toLowerCase().trim();
    if (!domain || !domain.includes('.')) return;
    const handle = setTimeout(async () => {
      try {
        // 1) Try company-by-domain first (more specific brand)
        const compRes = await axios.get(`${API_URL}/public/company-by-domain/${encodeURIComponent(domain)}`);
        if (compRes.data?.company_id) {
          const tenant = tenants.find((t) => t.id === compRes.data.tenant_id);
          if (tenant) {
            // Override tenant branding with company branding for login page preview
            setSelectedTenant({ ...tenant, branding: compRes.data.branding || tenant.branding });
            return;
          }
        }
        // 2) Fall back to tenant-by-domain
        const { data } = await axios.get(`${API_URL}/public/tenant-by-domain/${encodeURIComponent(domain)}`);
        if (data?.tenant_id) {
          const match = tenants.find((t) => t.id === data.tenant_id);
          if (match) setSelectedTenant({ ...match, branding: data.branding || match.branding });
        }
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(handle);
  }, [email, tenants, selectedTenant]);

  const primary = branding.primary_color || '#0052FF';
  const secondary = branding.secondary_color || '#0F172A';
  const accent = branding.accent_color || '#22C55E';
  const fontFamily = branding.font_family || 'Inter';
  const appName = branding.app_name || 'PayrollPro';
  const loginTitle = branding.login_title || 'Welcome back';
  const loginSubtitle = branding.login_subtitle || `Sign in to your ${appName} account`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const tenantId = selectedTenant?.id || '';
    const result = await login(email, password, tenantId);

    if (result.success) {
      navigate('/dashboard');
    } else {
      // If the backend says organization is required, prompt the user to select one.
      // This covers normal tenant users who left the org dropdown empty.
      // Super admins are allowed through by the backend without an org selection.
      const errMsg = result.error || '';
      if (errMsg.toLowerCase().includes('organization is required') && tenants.length > 0 && !selectedTenant) {
        setError('Please select an organization to continue');
      } else {
        setError(errMsg);
      }
    }

    setLoading(false);
  };

  const startSso = (kind) => {
    if (!selectedTenant) return;
    const url = `${API_URL}/sso/${kind}/${selectedTenant.id}/login`;
    window.location.href = url;
  };

  return (
    <div className="min-h-screen flex" style={{ fontFamily }}>
      {/* Left Side - Hero */}
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage:
            'url(https://hecs.in/storage/about-us/office-hds.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primary}33, ${secondary}66)` }}></div>
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-semibold tracking-tight mb-4">Enterprise Payroll Management</h1>
          <p className="text-lg text-slate-200">Secure, scalable, and compliant payroll processing for modern organizations</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="w-16 h-16 mx-auto mb-4 object-contain" />
            ) : (
              <img
                src="https://hecs.in/storage/logo/hecs-logo-1.png"
                alt="Logo"
                className="w-16 h-16 mx-auto mb-4"
              />
            )}
            <h2 className="text-3xl font-semibold tracking-tight" style={{ color: secondary }}>{loginTitle}</h2>
            <p className="mt-2 text-sm text-slate-600">{loginSubtitle}</p>
          </div>

          {/* Tenant picker */}
          {tenants.length > 0 && (
            <div data-testid="tenant-picker">
              <label className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wider">
                <Building2 className="w-3.5 h-3.5 inline mr-1" /> Organization <span className="text-red-500">*</span>
              </label>
              <select
                data-testid="tenant-select"
                value={selectedTenant?.id || ''}
                onChange={(e) => {
                  const t = tenants.find((x) => x.id === e.target.value);
                  setSelectedTenant(t || null);
                }}
                className="w-full h-11 px-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1"
                style={{ borderColor: selectedTenant ? primary : undefined }}
                required
              >
                <option value="">— Select Organization —</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{`${t.name}${t.code ? ` (${t.code})` : ''}`}</option>
                ))}
              </select>
            </div>
          )}

          {/* SSO buttons (only when tenant selected and SSO is enabled) */}
          {(samlEnabled || oidcEnabled) && (
            <div className="space-y-2" data-testid="sso-buttons">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-center">Single Sign-On</p>
              {samlEnabled && (
                <button
                  type="button"
                  data-testid="sso-saml-btn"
                  onClick={() => startSso('saml')}
                  className="w-full h-11 flex items-center justify-center gap-2 rounded-md text-white font-medium transition-colors"
                  style={{ background: secondary }}
                >
                  <ShieldCheck className="w-4 h-4" /> Sign in with SAML SSO
                </button>
              )}
              {oidcEnabled && (
                <button
                  type="button"
                  data-testid="sso-oidc-btn"
                  onClick={() => startSso('oidc')}
                  className="w-full h-11 flex items-center justify-center gap-2 rounded-md font-medium border-2 transition-colors"
                  style={{ borderColor: accent, color: accent }}
                >
                  <KeyRound className="w-4 h-4" /> Sign in with OIDC
                </button>
              )}
              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] uppercase text-slate-400 tracking-wider">or use password</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
              <input
                id="email" type="email" required data-testid="email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-4 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1"
                placeholder="you@company.com"
                style={{ borderColor: undefined }}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">Password</label>
              <div className="relative">
                <input
                  id="password" type={showPassword ? 'text' : 'password'} required data-testid="password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-4 pr-12 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-1"
                  placeholder="Enter your password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-11 text-white font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: primary }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-4 text-center text-xs text-slate-500">
            <p>{appName} © 2026 | Enterprise Payroll Suite</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
